/**
 * LegacyBuildingsIndex — Kendo-based Buildings listing (flag-OFF path).
 *
 * This is the original Buildings/Index implementation preserved verbatim from
 * develop. Import paths are the only change from the original index.jsx.
 * Do NOT apply logic edits here — any bug fix on the legacy path goes into the
 * shared helpers it consumes, not this file.
 *
 * Consumed exclusively by the Buildings/Index flag-aware wrapper (index.jsx)
 * when modernizedBuildingsListingsEnabled is false (default).
 */
import React, { useEffect, useCallback, useMemo, useReducer } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { TenantContextProvider } from '@contexts/tenant-context';
import { Grid, GridColumn as Column, GridToolbar } from '@progress/kendo-react-grid';
import FilterPanel from '@components/FilterPanel';
import SearchPanel from '@components/SearchPanel';
import I18n from '@utils/i18n';
import { KendoLocalizationProvider } from '@contexts/kendo-localization-context';
import api from '@utils/axios';
import useDebounce from '@hooks/useDebounce';
import { datatableBuildingsPath } from 'routes';
import ColumnMenu from '@components/Kendo/ColumnMenu';
import ColumnMenuContext from '@components/Kendo/ColumnMenuContext';
import EnterKeyPager from '@components/Kendo/EnterKeyPager';
import { saveCacheToLocalStorage } from '../../../../helpers/localStorage';
import { getInitialData } from '../helpers/initialData';
import useFilters from '../hooks/useFilters';
import kendoDatatableParams from '../../../../helpers/kendoDatatableParams';
import '../index.css';
import { usePublishListContext } from '@features/AIChatWidget/hooks/usePublishListContext';
import { actionTypes, reducer } from '../reducer';
import { ALL_COLUMNS, ALL_COLUMNS_DATA, INITIAL_FILTERS } from '../constants';
import CustomScrollbar from '../../../../components/CustomScrollbar/CustomScrollbar';
import MassActions from '../nested/MassActions';

function BuildingsIndex({ accountId, compliancePlatformsForSelect }) {
  const buildingsCacheKey = useMemo(() => {
    return accountId ? `kendo/buildings/index/account-${accountId}` : 'kendo/buildings/index';
  }, [accountId]);

  const initialData = useMemo(() => {
    const baseData = getInitialData(buildingsCacheKey);
    if (accountId) {
      return {
        ...baseData,
        filters: {
          ...baseData.filters,
          values: {
            ...baseData.filters.values,
            account: accountId
          }
        }
      };
    }
    return baseData;
  }, [buildingsCacheKey, accountId]);

  const [state, dispatch] = useReducer(reducer, {
    data: [],
    search: '',
    totalCount: 0,
    ...initialData
  });

  const { page, sort, filters, columns, data, totalCount, search, loading } = state;

  usePublishListContext({ page, sort, filters, totalCount, data, search });

  const debouncedFilters = useDebounce(filters);

  useEffect(() => {
    saveCacheToLocalStorage(buildingsCacheKey, { page, sort, filters, columns });
  }, [page, sort, filters, columns, buildingsCacheKey]);

  useEffect(() => {
    if (filters !== debouncedFilters) return undefined;

    dispatch({ type: actionTypes.LOADING_CHANGED, loading: true });
    const controller = new AbortController();

    api
      .get(datatableBuildingsPath(), {
        params: kendoDatatableParams({ page, sort, filters: debouncedFilters, search }),
        signal: controller.signal
      })
      .then(({ data: buildingsData }) => dispatch({ type: actionTypes.DATA_LOADED, data: buildingsData }))
      .catch((err) => {
        if (axios.isCancel(err)) return;
        dispatch({ type: actionTypes.LOADING_CHANGED, loading: false });
      });

    return () => controller.abort();
  }, [page, sort, filters, debouncedFilters, search]);

  const onSearchChange = (value) => {
    dispatch({ type: actionTypes.SEARCH_CHANGED, search: value });
  };

  const filtersList = useFilters({ filters, dispatch, hideAccountFilter: !!accountId, compliancePlatformsForSelect });

  const onColumnsChange = useCallback(
    (cols) => dispatch({ type: actionTypes.COLUMNS_CHANGED, columns: cols.map((col) => col.id) }),
    []
  );

  const onResetFilters = () => {
    dispatch({ type: actionTypes.FILTER_CHANGED, filters: INITIAL_FILTERS });
  };

  const onResetCache = () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(I18n.t('generic.are_you_sure'))) return;
    localStorage.removeItem(buildingsCacheKey);
    window.location.reload();
  };

  const allColumnsWrapped = useMemo(() => ALL_COLUMNS.map((column) => ALL_COLUMNS_DATA[column]), []);
  const columnsWrapped = useMemo(() => columns.map((column) => ALL_COLUMNS_DATA[column]), [columns]);

  const massActionQueryParams = useMemo(() => kendoDatatableParams({ filters, search, sort }), [filters, search, sort]);

  const columnContextValue = useMemo(
    () => ({
      onColumnsChange,
      columnsState: columnsWrapped,
      columns: allColumnsWrapped
    }),
    [columnsWrapped, allColumnsWrapped, onColumnsChange]
  );

  return (
    <KendoLocalizationProvider>
      <ColumnMenuContext.Provider value={columnContextValue}>
        <FilterPanel onResetFilters={onResetFilters} onResetCache={onResetCache} filters={filtersList}>
          <div className="filters__group--search">
            <SearchPanel placeholder={I18n.t('filters.search')} onChange={onSearchChange} />
          </div>
        </FilterPanel>
        {loading && (
          <div className="workspace__section">
            <div className="qmb-loading--96" data-title={I18n.t('generic.loading')}>
              <svg role="img">
                <use href="/map.svg#load-spinner" />
              </svg>
            </div>
          </div>
        )}
        {!loading && (
          <CustomScrollbar>
            <Grid
              className="qmb-grid--01"
              data={data}
              total={totalCount}
              skip={page.skip}
              take={page.take}
              onPageChange={(event) => {
                dispatch({ type: actionTypes.PAGE_CHANGED, page: event.page });
              }}
              pageable={{
                type: 'input',
                pageSizes: [25, 50, 100],
                pageSizeValue: page.take
              }}
              pager={EnterKeyPager}
              sortable
              sort={sort}
              onSortChange={(event) => {
                dispatch({ type: actionTypes.SORT_CHANGED, sort: event.sort });
              }}>
              <GridToolbar>
                <MassActions checkedIds={[]} queryParams={massActionQueryParams} />
              </GridToolbar>
              {columnsWrapped.map((column) => (
                <Column
                  key={column.id}
                  field={column.field}
                  title={column.title}
                  columnMenu={ColumnMenu}
                  cell={column.cell}
                  sortable={column.sortable}
                  width={column.width}
                />
              ))}
            </Grid>
          </CustomScrollbar>
        )}
      </ColumnMenuContext.Provider>
    </KendoLocalizationProvider>
  );
}

BuildingsIndex.propTypes = {
  compliancePlatformsForSelect: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.any
    })
  ),
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
};

BuildingsIndex.defaultProps = {
  compliancePlatformsForSelect: [],
  accountId: null
};

function LegacyBuildingsIndex(props) {
  return (
    <TenantContextProvider>
      {/* eslint-disable-next-line react/jsx-props-no-spreading */}
      <BuildingsIndex {...props} />
    </TenantContextProvider>
  );
}

export default LegacyBuildingsIndex;
